import { Flex, Kbd, useComputedColorScheme } from "@mantine/core";

const TOP_BAR_KBD_STYLES = {
  backgroundColor: "color-mix(in srgb, rgb(14 17 29) 90%, rgb(118 93 217) 10%)",
  color: "color-mix(in srgb, rgb(255 255 255) 96%, rgb(215 206 246) 4%)",
  border: "1px solid color-mix(in srgb, rgb(255 255 255) 8%, rgb(36 41 61) 92%)",
  boxShadow:
    "0 1px 0 color-mix(in srgb, rgb(0 0 0) 30%, transparent), inset 0 1px 0 color-mix(in srgb, rgb(255 255 255) 12%, transparent)",
} as const;

const TOP_BAR_PLUS_COLOR = "color-mix(in srgb, rgb(255 255 255) 92%, rgb(215 206 246) 8%)";

function getDefaultKbdStyles(colorScheme: "light" | "dark") {
  if (colorScheme === "dark") {
    return {
      backgroundColor: "var(--mantine-color-gray-0)",
      color: "var(--mantine-color-gray-7)",
      borderColor: "var(--mantine-color-gray-3)",
    } as const;
  }
  return {
    backgroundColor: "var(--mantine-color-dark-6)",
    color: "var(--mantine-color-dark-0)",
    borderColor: "var(--mantine-color-dark-4)",
  } as const;
}

function getDefaultPlusColor(colorScheme: "light" | "dark") {
  if (colorScheme === "dark") {
    return "var(--mantine-color-gray-6)";
  }
  return "var(--mantine-color-dark-2)";
}

export type KeyboardShortcutProps = {
  shortcut: string;
  separator?: string;
  variant?: "default" | "topBar";
};

export const KeyboardShortcut = ({
  shortcut,
  separator = "+",
  variant = "default",
}: KeyboardShortcutProps) => {
  const colorScheme = useComputedColorScheme("light");
  const normalizedShortcut = shortcut.replace(/mod/g, "⌘");

  const keys: string[] = (() => {
    if (normalizedShortcut.includes(separator)) {
      return normalizedShortcut.split(separator);
    }
    const match = normalizedShortcut.match(/^(⌘)(.+)$/);
    if (match) {
      return [match[1], match[2]];
    }
    return [normalizedShortcut];
  })();

  const defaultKbdRoot = getDefaultKbdStyles(colorScheme);
  const defaultPlusColor = getDefaultPlusColor(colorScheme);

  return (
    <Flex align="center" gap="0.25rem" display="inline-flex">
      {keys
        .flatMap((key, index) => [
          <Kbd
            key={key.trim()}
            size="xs"
            styles={{
              root: variant === "topBar" ? TOP_BAR_KBD_STYLES : defaultKbdRoot,
            }}
          >
            {key.trim()}
          </Kbd>,
          index < keys.length - 1 && (
            <span
              key={`sep-${key.trim()}`}
              style={
                variant === "topBar"
                  ? { color: TOP_BAR_PLUS_COLOR, fontWeight: 600 }
                  : { color: defaultPlusColor, fontWeight: 600 }
              }
            >
              {separator}
            </span>
          ),
        ])
        .filter(Boolean)}
    </Flex>
  );
};
